import React from 'react'
import {useSelector} from 'react-redux'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {CompactFooter} from 'components/CompactFooter'
import {Footer} from 'components/Footer'
import {SrpFAQs} from 'components/Seo/SrpFAQs'
import {SrpLinks} from 'components/Seo/SrpLinks'
import {SrpTopHotelsLinks} from 'components/Seo/SrpTopHotelsLinks'
import {TrustpilotBar} from 'components/Trustpilot'
import {useDefaultLayout} from 'hooks/useDefaultLayout'
import {useIsSeoSRP} from 'hooks/useIsSeoSRP'
import {useTopHeaderHeight} from 'hooks/useTopHeaderHeight'
import {
  getIsTopHeaderHidden,
  getSearchDesktopHeaderHeight
} from 'modules/search/selectors'
import {LayoutType} from 'modules/search/types'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'

const LIST_LAYOUT_WIDTH = 920
const SPLIT_LAYOUT_LIST_WIDTH = 877

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: ${cssTheme.layout.spacing.s400};
  flex: 1 auto;

  &.--hidden {
    visibility: hidden;
  }

  ${linariaMq.desktopSm} {
    max-width: ${LIST_LAYOUT_WIDTH}px;
    padding: ${cssTheme.layout.spacing.s400};
    padding-top: ${cssTheme.layout.spacing.s600};
  }
`

const MapWrapper = styled.div`
  background-color: ${cssTheme.colors.background.neutral.primary};
`

const SplitLayout = styled.main`
  display: flex;

  ${ListWrapper} {
    z-index: 1;
    flex: 1 auto;
    display: flex;
    flex-direction: column;

    ${linariaMq.desktopSm} {
      min-width: ${SPLIT_LAYOUT_LIST_WIDTH}px;
    }
  }

  ${MapWrapper} {
    display: flex;
    position: sticky;
    top: var(--searchDesktopHeaderHeight);
    width: 100%;
    height: calc(100vh - var(--searchDesktopHeaderHeight));
  }
`

const FooterWrapper = styled.div`
  position: relative;
  z-index: 1;
`

const ListLayout = styled.main`
  flex: 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const MapLayout = styled.div`
  ${MapWrapper} {
    display: flex;
    position: fixed;
    top: 0;
    width: 100%;
    min-height: stretch;

    ${linariaMq.desktopXs} {
      top: var(--searchDesktopHeaderHeight);
      height: calc(100vh - var(--searchDesktopHeaderHeight));
      min-height: unset;
    }

    ${linariaMq.desktopSm} {
      top: var(--searchDesktopHeaderHeight);
      height: calc(100vh - var(--searchDesktopHeaderHeight));
      min-height: unset;
    }
  }
`

const LayoutMap: Record<LayoutType, typeof ListLayout> = {
  [LayoutType.LIST]: ListLayout,
  [LayoutType.MAP]: MapLayout,
  [LayoutType.SPLIT]: SplitLayout
}

interface Props {
  listComponent: React.ReactNode
  mapComponent: React.ReactNode
}

/**
 * Container to handle which type of view to display
 */
export const SearchLayoutContainer: React.FC<Props> = ({
  listComponent,
  mapComponent
}) => {
  const {layout, isListVisible, isMapVisible} = useDefaultLayout()
  const searchDesktopHeaderHeight = useSelector(getSearchDesktopHeaderHeight)
  const topHeaderIsHidden = useSelector(getIsTopHeaderHidden)
  const {isMobile, isDesktopXs} = useDeviceLayout()
  const {brand} = useBrand()
  const {search} = useBrandCapabilities()

  const isSeoSRP = useIsSeoSRP()

  const topHeaderHeight = useTopHeaderHeight()

  const searchDesktopHeaderHeightValue = topHeaderIsHidden
    ? searchDesktopHeaderHeight - topHeaderHeight // when the top header is hidden we need to fill the space. This is needed to make the map grow on the SRP
    : searchDesktopHeaderHeight

  const LayoutComponent = LayoutMap[layout]
  const isMobileLayout = isMobile || isDesktopXs

  return (
    <>
      {layout !== LayoutType.MAP && !isMobileLayout && <TrustpilotBar />}
      <LayoutComponent
        style={{
          // @ts-expect-error -- need to find a way to type this
          '--searchDesktopHeaderHeight': searchDesktopHeaderHeightValue + 'px'
        }}
      >
        <ListWrapper className={cx(!isListVisible && '--hidden')}>
          {listComponent}
          {search.hasSeoFaqs && isSeoSRP && <SrpFAQs />}
        </ListWrapper>
        {isMapVisible && !isMobile && <MapWrapper>{mapComponent}</MapWrapper>}
      </LayoutComponent>
      {layout !== LayoutType.MAP && (
        <FooterWrapper>
          {search.hasSeoUrls ? (
            <Footer
              brand={brand}
              linksSlot={
                isSeoSRP && (
                  <>
                    <SrpTopHotelsLinks />
                    <SrpLinks />
                  </>
                )
              }
            />
          ) : (
            <CompactFooter />
          )}
        </FooterWrapper>
      )}
    </>
  )
}
